import { HostTutorBookingResponse } from '../models/Tutor';
import { hostBookingStartIsPast } from './hostRoomBookingGrouping';
import { isOnlinePaymentHold } from './seriesAcceptPlan';
import { isTutorBookingModeratable } from './tutorBookingStatus';

export type HostTutorBookingDisplayRow =
  | { kind: 'single'; booking: HostTutorBookingResponse }
  | { kind: 'series'; seriesId: string; bookings: HostTutorBookingResponse[] };

/** A future lesson that still needs the first accept or decline. */
export function tutorBookingGroupsAsPendingSeries(
  booking: HostTutorBookingResponse,
  now: Date,
): boolean {
  const seriesId = booking.recurringSeriesId?.trim();
  if (!seriesId) return false;
  if (!isTutorBookingModeratable(booking.status)) return false;
  return !hostBookingStartIsPast(booking.startTime, now);
}

/** A still-pending online lesson cannot be accepted or declined on its own. */
export function pendingOnlineTutorUsesSeriesEndpoint(
  booking: HostTutorBookingResponse,
  now: Date = new Date(),
): boolean {
  if (!booking.recurringSeriesId?.trim()) return false;
  if (!isOnlinePaymentHold(booking.paymentStatus)) return false;
  return tutorBookingGroupsAsPendingSeries(booking, now);
}

/**
 * Two or more future pending lessons of one series stay one card.
 * A lone pending lesson, and any lesson already confirmed, declined, or in
 * the past, is its own booking.
 */
export function groupHostTutorBookingRequests(
  items: HostTutorBookingResponse[],
  now: Date = new Date(),
): HostTutorBookingDisplayRow[] {
  if (items.length === 0) return [];
  const bySeries = new Map<string, HostTutorBookingResponse[]>();
  for (const booking of items) {
    if (!tutorBookingGroupsAsPendingSeries(booking, now)) continue;
    const seriesId = booking.recurringSeriesId!.trim();
    const list = bySeries.get(seriesId);
    if (list) list.push(booking);
    else bySeries.set(seriesId, [booking]);
  }
  Array.from(bySeries.values()).forEach((list) => {
    list.sort((a, b) => a.startTime.localeCompare(b.startTime));
  });
  const seriesIds = new Set(
    Array.from(bySeries.entries())
      .filter((entry) => entry[1].length >= 2)
      .map((entry) => entry[0]),
  );
  const emitted = new Set<string>();
  const out: HostTutorBookingDisplayRow[] = [];
  for (const booking of items) {
    const seriesId = booking.recurringSeriesId?.trim();
    const grouped =
      seriesId != null &&
      seriesIds.has(seriesId) &&
      tutorBookingGroupsAsPendingSeries(booking, now);
    if (grouped) {
      if (!emitted.has(seriesId)) {
        emitted.add(seriesId);
        out.push({
          kind: 'series',
          seriesId,
          bookings: bySeries.get(seriesId)!,
        });
      }
    } else {
      out.push({ kind: 'single', booking });
    }
  }
  return out;
}
